import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../components/FeatherIcon'
import { LoadingBlock } from '../components/Spinner'
import type { OfApi } from './ofApi'
import { hms, toStats, type PostStats } from './posts'

/** OnlyFans' "POST STATISTICS" dialog: GET posts/<id>/stats?withChart=true. */
export function PostStatsModal({ of, postId, referer, onClose }: { of: OfApi; postId: number; referer: string; onClose: () => void }) {
  const { t, i18n } = useTranslation('ofPages')
  const [stats, setStats] = useState<PostStats | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let live = true
    of.get<Record<string, unknown>>(`posts/${postId}/stats`, { withChart: 'true' }, referer)
      .then((raw) => live && setStats(toStats(raw ?? {})))
      .catch(() => live && setFailed(true))
    return () => {
      live = false
    }
  }, [of, postId, referer])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const n = (v: number) => v.toLocaleString(i18n.language)
  const usd = (v: number) => `$${v.toLocaleString(i18n.language, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  return (
    <div className="ofp-modal-back" onClick={onClose}>
      <div className="ofp-modal" role="dialog" aria-modal="true" aria-label={t('stats.title')} onClick={(e) => e.stopPropagation()}>
        <div className="ofp-modal-head">
          <h3 className="ofp-modal-title">{t('stats.title')}</h3>
          <button type="button" className="lib-icon-btn" aria-label={t('close')} onClick={onClose}>
            <FeatherIcon name="x" size={18} />
          </button>
        </div>
        <p className="ofp-modal-note">{t('stats.note')}</p>
        {failed && <p className="lib-empty">{t('error')}</p>}
        {!stats && !failed && <LoadingBlock pad="32px 0" label={t('loading')} />}
        {stats && (
          <dl className="ofp-stats">
            <Row label={t('stats.purchases')} value={`${n(stats.purchases)} (${usd(stats.purchasesSum)})`} />
            <Row label={t('stats.views')} value={n(stats.views)} sub={t('stats.unique', { n: n(stats.uniqueViews) })} />
            <Row label={t('stats.likes')} value={n(stats.likes)} />
            <Row label={t('stats.comments')} value={n(stats.comments)} />
            <Row label={t('stats.tips')} value={`${n(stats.tipCount)} (${usd(stats.tipSum)})`} />
            <Row label={t('stats.duration')} value={hms(stats.duration)} sub={t('stats.average', { v: hms(stats.durationAverage) })} />
          </dl>
        )}
      </div>
    </div>
  )
}

function Row({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="ofp-stats-row">
      <dt>{label}</dt>
      <dd>
        {value}
        {sub && <span className="ofp-stats-sub">{sub}</span>}
      </dd>
    </div>
  )
}
